import React, { useState, useRef, useEffect, useCallback } from "react";
import { Sparkles, X, Send, Zap, Lightbulb, TrendingUp, Clock, Wand2 } from "lucide-react";
import { useAura } from "@/context/AuraContext";
import { streamCoach, contentIdeas } from "@/lib/ai";
import { cn } from "@/lib/utils";

const QUICK = [
  { icon: TrendingUp, label: "Grow my Aura", prompt: "Give me 3 specific things I can do this week to grow my Aura on AuraMax." },
  { icon: Wand2, label: "Punch up my hook", prompt: "Help me write a scroll-stopping hook for my next video. Ask me what it's about first." },
  { icon: Clock, label: "Best time to post", prompt: "When are the best times to post and go Live to maximize reach?" },
];

// tiny **bold** + newline renderer
function Rich({ text }) {
  return (
    <>
      {text.split("\n").map((line, i) => (
        <span key={`${i}:${line.slice(0, 24)}`} className="block">
          {line.split(/(\*\*[^*]+\*\*)/g).map((part, j) =>
            part.startsWith("**") && part.endsWith("**")
              ? <strong key={`${j}:${part}`} className="font-semibold text-white">{part.slice(2, -2)}</strong>
              : <span key={`${j}:${part}`}>{part}</span>
          )}
        </span>
      ))}
    </>
  );
}

export function AuraCoach() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sessionRef = useRef(null);
  const scrollRef = useRef(null);
  const { profile } = useAura();

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = useCallback(async (raw) => {
    const message = (raw ?? input).trim();
    if (!message || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { id: `u${Date.now()}`, role: "user", text: message }, { id: `a${Date.now()}`, role: "assistant", text: "" }]);
    try {
      await streamCoach({
        sessionId: sessionRef.current,
        message,
        onSession: (sid) => { sessionRef.current = sid; },
        onDelta: (d) => setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { ...copy[copy.length - 1], text: copy[copy.length - 1].text + d };
          return copy;
        }),
      });
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", text: e.message || "Aura Coach is unavailable right now." };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  }, [input, busy]);

  const getIdeas = useCallback(async () => {
    if (busy) return;
    const interest = profile.interests?.[0] || "Lifestyle";
    setBusy(true);
    setMessages((m) => [...m, { id: `u${Date.now()}`, role: "user", text: `Content ideas for ${interest}` }, { id: `a${Date.now()}`, role: "assistant", text: "", loading: true }]);
    try {
      const { ideas } = await contentIdeas(interest, 5);
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", text: `Fresh ${interest} ideas to post today:`, ideas };
        return copy;
      });
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", text: e.message || "Couldn't load ideas." };
        return copy;
      });
    } finally { setBusy(false); }
  }, [busy, profile.interests]);

  return (
    <>
      {/* Trigger — lives on the user's own profile */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="aura-coach-open"
          className="flex items-center gap-2 rounded-full border border-am-border-strong bg-am-surface2 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-white/5"
        >
          <Sparkles className="h-4 w-4 aura-text" strokeWidth={2.2} /> Aura Coach
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          data-testid="aura-coach-panel"
          className="fixed inset-x-0 bottom-0 z-[60] flex h-[86vh] flex-col overflow-hidden border border-am-border bg-am-bg shadow-2xl sm:inset-x-auto sm:bottom-6 sm:right-6 sm:h-[600px] sm:w-[390px] sm:rounded-3xl"
        >
          {/* header */}
          <div className="relative flex items-center gap-3 border-b border-am-border px-4 py-3.5">
            <div className="pointer-events-none absolute left-0 top-0 h-16 w-40 aura-radial opacity-[0.10] blur-2xl" />
            <span className="grid h-9 w-9 place-items-center rounded-xl aura-gradient"><Sparkles className="h-5 w-5 text-black" strokeWidth={2.2} /></span>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-bold text-white">Aura Coach</div>
              <div className="text-xs text-am-text2">Your growth assistant</div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="aura-coach-close" className="grid h-8 w-8 place-items-center rounded-full text-am-text2 hover:bg-white/5 hover:text-white">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 thin-scroll">
            {messages.length === 0 ? (
              <div className="flex h-full flex-col">
                <div className="mb-5">
                  <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-white"><Zap className="h-4 w-4 aura-text" /> Hey — let's max your Aura.</div>
                  <p className="text-sm leading-relaxed text-am-text2">Ask me anything about growing your audience, captions, hooks, going Live, or what to post next.</p>
                </div>
                <div className="flex flex-col gap-2">
                  <button onClick={getIdeas} data-testid="coach-quick-ideas" className="flex items-center gap-3 rounded-2xl border border-am-border bg-am-surface/60 px-4 py-3 text-left text-sm font-medium text-white transition-colors hover:bg-white/5">
                    <Lightbulb className="h-4 w-4 aura-text" /> Ideas for my niche
                  </button>
                  {QUICK.map((q) => (
                    <button key={q.label} onClick={() => send(q.prompt)} data-testid={`coach-quick-${q.label.split(" ")[0].toLowerCase()}`} className="flex items-center gap-3 rounded-2xl border border-am-border bg-am-surface/60 px-4 py-3 text-left text-sm font-medium text-white transition-colors hover:bg-white/5">
                      <q.icon className="h-4 w-4 aura-text" /> {q.label}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {messages.map((m, i) => (
                  <div key={m.id || `m${i}`} className={cn("flex", m.role === "user" ? "justify-end" : "justify-start")}>
                    <div
                      className={cn(
                        "max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed",
                        m.role === "user" ? "bg-white text-black" : "border border-am-border bg-am-surface text-am-text"
                      )}
                    >
                      {m.role === "assistant" && m.text === "" && !m.ideas ? (
                        <span className="flex gap-1 py-1">
                          <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-am-text3" style={{ animationDelay: "0ms" }} />
                          <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-am-text3" style={{ animationDelay: "150ms" }} />
                          <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-am-text3" style={{ animationDelay: "300ms" }} />
                        </span>
                      ) : (
                        <Rich text={m.text} />
                      )}
                      {m.ideas && (
                        <div className="mt-3 flex flex-col gap-2">
                          {m.ideas.map((idea, k) => (
                            <div key={idea.title || idea.text || `idea${k}`} className="rounded-xl border border-am-border bg-am-bg/60 p-3">
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-sm font-semibold text-white">{idea.title}</span>
                                {idea.format && <span className="shrink-0 rounded-full border border-am-border-strong px-2 py-0.5 text-[10px] font-semibold aura-text">{idea.format}</span>}
                              </div>
                              <p className="mt-1 text-xs text-am-text2">{idea.description}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* input */}
          <div className="flex items-center gap-2 border-t border-am-border p-3">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              data-testid="coach-input"
              placeholder="Ask Aura Coach…"
              className="flex-1 rounded-full border border-am-border bg-am-surface2 px-4 py-2.5 text-sm text-white placeholder:text-am-text3 outline-none focus:border-am-border-strong"
            />
            <button onClick={() => send()} disabled={busy || !input.trim()} data-testid="coach-send" className="grid h-10 w-10 shrink-0 place-items-center rounded-full aura-gradient text-black transition-transform active:scale-90 disabled:opacity-40">
              <Send className="h-4 w-4" strokeWidth={2.4} />
            </button>
          </div>
          <p className="pb-2 text-center text-[10px] text-am-text3">Aura Coach can make mistakes. Powered by AuraMax AI.</p>
        </div>
      )}
    </>
  );
}
